
import React, { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { Loader2, Eye, EyeOff, Download } from 'lucide-react';
import { profileTypeMapping } from '@/utils/profileTypeMapping';
import DraggableProfileCard from './DraggableProfileCard';
import { generateExampleScores } from '@/utils/exampleScoreGenerator';
import { useProfileAverages } from '@/hooks/useProfileAverages';
import { useProfileOrders } from '@/hooks/useProfileOrders';
import { PROFILE_CATEGORIES, ProfileCategory } from '@/utils/profileCategories';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { exportProfilesToPDF } from '@/utils/profilesPdfExport';
import { useToast } from '@/hooks/use-toast';

const ProfilesGrid: React.FC = () => {
  const { averages, loading: averagesLoading } = useProfileAverages();
  const { isSuperAdmin } = useAuth();
  const [showSampleSize, setShowSampleSize] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();
  const { 
    profileOrders, 
    loading: ordersLoading, 
    saving, 
    saveProfileOrders, 
    resetToDefault 
  } = useProfileOrders();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sort profiles based on custom order or default alphabetical order
  const sortedProfiles = useMemo(() => {
    const profileEntries = Object.entries(profileTypeMapping);
    
    if (profileOrders.length === 0) {
      // Default alphabetical sorting by profile type
      return profileEntries.sort(([, a], [, b]) => a.type.localeCompare(b.type));
    }
    
    // Sort by custom order
    const orderedProfiles: Array<[string, typeof profileTypeMapping[keyof typeof profileTypeMapping]]> = [];
    const unorderedProfiles: Array<[string, typeof profileTypeMapping[keyof typeof profileTypeMapping]]> = [];
    
    // First, add profiles in custom order
    profileOrders
      .sort((a, b) => a.position - b.position)
      .forEach(order => {
        const profile = profileEntries.find(([pattern]) => pattern === order.disc_code);
        if (profile) {
          orderedProfiles.push(profile);
        }
      });
    
    // Then add any profiles not in custom order
    profileEntries.forEach(profile => {
      if (!profileOrders.some(order => order.disc_code === profile[0])) {
        unorderedProfiles.push(profile);
      }
    });
    
    // Sort unordered profiles alphabetically
    unorderedProfiles.sort(([, a], [, b]) => a.type.localeCompare(b.type));
    
    return [...orderedProfiles, ...unorderedProfiles];
  }, [profileOrders]);

  // Group profiles by category - MUST be before any early returns
  const profilesByCategory = useMemo(() => {
    const grouped: Record<ProfileCategory, typeof sortedProfiles> = {
      GAS: [],
      GLUE: [],
      BRAKE: []
    };
    
    sortedProfiles.forEach((profile) => {
      const category = profile[1].category;
      grouped[category].push(profile);
    });
    
    return grouped;
  }, [sortedProfiles]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedProfiles.findIndex(([pattern]) => pattern === active.id);
      const newIndex = sortedProfiles.findIndex(([pattern]) => pattern === over.id);
      
      const newOrder = arrayMove(sortedProfiles, oldIndex, newIndex);
      
      // Save the new order
      const ordersToSave = newOrder.map(([pattern], index) => ({
        discCode: pattern,
        position: index
      }));
      
      saveProfileOrders(ordersToSave);
    }
  };

  if (averagesLoading || ordersLoading) {
    return (
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 16 }).map((_, index) => (
          <div key={index} className="h-80 bg-muted/30 animate-pulse rounded-lg border border-border/30">
            <div className="p-6 space-y-4">
              <div className="h-4 bg-muted-foreground/10 rounded"></div>
              <div className="h-3 bg-muted-foreground/10 rounded w-3/4"></div>
              <div className="h-32 bg-muted-foreground/10 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // Prepare profiles data for export
      const profilesData = sortedProfiles.map(([pattern, profileData]) => {
        const profileName = profileData.type;
        const realAverages = averages[profileName];
        const isRealData = !!realAverages;
        const scores = realAverages || generateExampleScores(pattern);
        
        return {
          pattern,
          profileData,
          scores,
          isRealData
        };
      });

      await exportProfilesToPDF(profilesData);
      toast({
        title: "Export Complete",
        description: "Behavioral profiles PDF has been downloaded.",
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export Failed",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Super Admin Controls */}
      {isSuperAdmin() && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-2 text-muted-foreground"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSampleSize(!showSampleSize)}
            className="flex items-center gap-2 text-muted-foreground"
          >
            {showSampleSize ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showSampleSize ? 'Hide Sample Sizes' : 'Show Sample Sizes'}
          </Button>
        </div>
      )}

      {/* Category-grouped Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedProfiles.map(([pattern]) => pattern)}
          strategy={rectSortingStrategy}
        >
          <div className="space-y-12">
            {(['GAS', 'GLUE', 'BRAKE'] as ProfileCategory[]).map((category) => {
              const categoryProfiles = profilesByCategory[category];
              const categoryInfo = PROFILE_CATEGORIES[category];
              
              return (
                <div key={category} className="space-y-4">
                  {/* Category Header */}
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{categoryInfo.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-3">
                        <h2 className="text-xl font-semibold text-foreground uppercase tracking-wide">
                          {categoryInfo.name}
                        </h2>
                        <span className="text-sm text-muted-foreground">
                          {categoryInfo.description}
                        </span>
                        <span className="text-sm text-muted-foreground ml-auto">
                          {categoryProfiles.length} profile{categoryProfiles.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div 
                        className="h-0.5 mt-2 rounded-full"
                        style={{ backgroundColor: categoryInfo.color }}
                      />
                    </div>
                  </div>

                  {/* Profiles Grid */}
                  <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {categoryProfiles.map(([pattern, profileData]) => {
                      const profileName = profileData.type;
                      const realAverages = averages[profileName];
                      const isRealData = !!realAverages;
                      const scores = realAverages || generateExampleScores(pattern);
                      
                      return (
                        <DraggableProfileCard
                          key={pattern}
                          id={pattern}
                          pattern={pattern}
                          profileData={profileData}
                          exampleScores={scores}
                          isRealData={isRealData}
                          showSampleSize={showSampleSize}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      
      {saving && (
        <div className="fixed bottom-6 right-6 bg-background border border-border text-foreground px-4 py-2 rounded-lg shadow-sm flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span className="text-sm">Saving order...</span>
        </div>
      )}
    </div>
  );
};

export default ProfilesGrid;
